import { type FC } from 'react';
import { useUnit } from 'effector-react';
import { Pagination, ScrollArea, Stack } from '@mantine/core';
import {
    type PayoutCascadeTemplate,
    type PayoutCascadeTemplateFilters,
    usePayoutCascadeTemplatesList,
} from '~/entities/payout';
import { type PaginationModel } from '~/shared/lib/pagination';
import { Table } from '~/shared/ui/Table';
import { createTableData } from './tableUtils';

interface CascadeTemplatesListProps {
    filter?: PayoutCascadeTemplateFilters;
    paginationModel: PaginationModel;
    onClick?: (template: PayoutCascadeTemplate) => void;
}

export const CascadeTemplatesList: FC<CascadeTemplatesListProps> = ({ filter, onClick, paginationModel }) => {
    const pagination = useUnit(paginationModel.$pagination);

    const templates = usePayoutCascadeTemplatesList({
        'pagination.page': pagination.page.toString(),
        'pagination.perPage': pagination.perPage.toString(),
        ...(filter?.name?.value && { 'filter.name.value': filter.name.value }),
        ...(filter?.id?.value && { 'filter.id.value': filter.id.value }),
    });

    const total = Number(templates.data?.pagination?.total);
    const totalPages = Math.ceil(total / pagination.perPage);

    const tableData = createTableData({
        templates: templates.data?.items,
    });

    const handleRowClick = (item: PayoutCascadeTemplate) => {
        return onClick?.(item);
    };

    const handleChangeCurrentPage = (page: number) => {
        paginationModel.setPage(page - 1);
    };

    return (
        <Stack>
            <ScrollArea h={512}>
                <Table
                    striped
                    highlightOnHover
                    data={tableData}
                    onRowClick={handleRowClick}
                    loading={templates.isLoading}
                />
            </ScrollArea>

            {totalPages > 1 && (
                <Pagination
                    total={totalPages}
                    value={pagination.page + 1}
                    onChange={handleChangeCurrentPage}
                    size='sm'
                />
            )}
        </Stack>
    );
};
